import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

// Local Post interface that explicitly includes archived status
interface Post {
  id: string;
  title: string;
  slug: string;
  body: string;
  meta_description: string;
  featured_image_url: string;
  featured_image_base64?: string;
  status: 'draft' | 'published' | 'archived';
  tags?: string;
  views: number;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

// Ensure TypeScript recognizes the archived status
type PostStatus = 'draft' | 'published' | 'archived';

interface DashboardProps {
  token: string;
  user: { id: string; email: string };
  onLogout: () => void;
  apiBaseUrl: string;
}

const Dashboard: React.FC<DashboardProps> = ({ token, user, onLogout, apiBaseUrl }) => {
  const navigate = useNavigate();
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<PostStatus>('draft');

  const fetchAllPosts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl.replace('/api/public', '')}/api/posts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setAllPosts(data.data);
      } else {
        setError(data.message || 'Failed to fetch posts');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAllPosts();
  }, [fetchAllPosts]);

  // Filter posts based on current status filter
  const posts = allPosts.filter(post => post.status === statusFilter);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleEdit = (postId: string) => {
    navigate(`/cms/edit/${postId}`);
  };

  const handleUnarchive = async (postId: string) => {
    try {
      const response = await fetch(`${apiBaseUrl.replace('/api/public', '')}/api/posts/${postId}/unarchive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh the posts list
        fetchAllPosts();
        
        // Show success message
        const successMsg = document.createElement('div');
        successMsg.textContent = 'Post unarchived successfully!';
        successMsg.className = 'success-message';
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 2000);
      } else {
        setError(data.message || 'Failed to unarchive post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleArchive = async (postId: string) => {
    try {
      const response = await fetch(`${apiBaseUrl.replace('/api/public', '')}/api/posts/${postId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh the posts list
        fetchAllPosts();
        
        // Show success message
        const successMsg = document.createElement('div');
        successMsg.textContent = 'Post archived successfully!';
        successMsg.className = 'success-message';
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 2000);
      } else {
        setError(data.message || 'Failed to archive post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleUnpublish = async (postId: string) => {
    try {
      const response = await fetch(`${apiBaseUrl.replace('/api/public', '')}/api/posts/${postId}/unpublish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh the posts list
        fetchAllPosts();
        
        // Show success message
        const successMsg = document.createElement('div');
        successMsg.textContent = 'Post unpublished successfully!';
        successMsg.className = 'success-message';
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 2000);
      } else {
        setError(data.message || 'Failed to unpublish post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handlePublish = async (postId: string) => {
    try {
      const response = await fetch(`${apiBaseUrl.replace('/api/public', '')}/api/posts/${postId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'published',
          published_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh the posts list
        fetchAllPosts();
        
        // Show success message
        const successMsg = document.createElement('div');
        successMsg.textContent = 'Post published successfully!';
        successMsg.className = 'success-message';
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 2000);
      } else {
        setError(data.message || 'Failed to publish post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>SolveSmartHome CMS</h1>
          <div className="user-info">
            <span>Welcome, {user.email}</span>
            <button onClick={onLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-content">
          <div className="dashboard-header-section">
            <h2>Content Management</h2>
            <div className="dashboard-controls">
              <div className="status-filters">
                <button
                  className={`filter-button ${statusFilter === 'draft' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('draft')}
                >
                  Drafts ({allPosts.filter(p => p.status === 'draft').length})
                </button>
                <button
                  className={`filter-button ${statusFilter === 'published' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('published')}
                >
                  Published ({allPosts.filter(p => p.status === 'published').length})
                </button>
                <button
                  className={`filter-button ${statusFilter === 'archived' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('archived')}
                >
                  Archived ({allPosts.filter(p => p.status === 'archived').length})
                </button>
              </div>
              <button 
                className="settings-button"
                onClick={() => navigate('/cms/settings')}
              >
                ⚙️ Settings
              </button>
            </div>
          </div>

          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading posts...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={fetchAllPosts} className="retry-button">
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && (
            <div className="posts-list">
              {posts.length === 0 ? (
                <div className="empty-state">
                  <h3>No {statusFilter} posts found</h3>
                  <p>
                    {statusFilter === 'draft' 
                      ? 'Create your first draft using the ingestion API or wait for content to be added.'
                      : 'No published posts yet. Publish some drafts to see them here.'
                    }
                  </p>
                </div>
              ) : (
                posts.map((post) => (
                  <div key={post.id} className="post-card">
                    <div className="post-header">
                      <h3 className="post-title">{post.title}</h3>
                      <span className={`post-status ${post.status}`}>
                        {post.status}
                      </span>
                    </div>
                    {(post.featured_image_url || post.featured_image_base64) && (
                      <div className="post-image">
                        <img 
                          src={post.featured_image_base64 || post.featured_image_url} 
                          alt={post.title}
                          className="post-thumbnail"
                        />
                      </div>
                    )}
                    <div className="post-meta">
                      <span className="post-slug">/{post.slug}</span>
                      <span className="post-date">
                        Created: {formatDate(post.created_at)}
                      </span>
                      {post.updated_at !== post.created_at && (
                        <span className="post-date">
                          Updated: {formatDate(post.updated_at)}
                        </span>
                      )}
                    </div>
                    {post.meta_description && (
                      <p className="post-description">{post.meta_description}</p>
                    )}
                    <div className="post-actions">
                      <button 
                        className="action-button primary"
                        onClick={() => handleEdit(post.id)}
                      >
                        Edit
                      </button>
                      {post.status === 'draft' && (
                        <button 
                          className="action-button secondary"
                          onClick={() => handlePublish(post.id)}
                        >
                          Publish
                        </button>
                      )}
                      {post.status === 'published' && (
                        <button 
                          className="action-button warning"
                          onClick={() => handleUnpublish(post.id)}
                        >
                          Unpublish
                        </button>
                      )}
                      {(post.status === 'draft' || post.status === 'published') && (
                        <button 
                          className="action-button danger"
                          onClick={() => handleArchive(post.id)}
                        >
                          Archive
                        </button>
                      )}
                      {post.status === 'archived' && (
                        <button 
                          className="action-button secondary"
                          onClick={() => handleUnarchive(post.id)}
                        >
                          Unarchive
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
